import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ClipboardList, ArrowUpRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/collections", label: "Collections" },
  { to: "/brands", label: "Brands" },
  { to: "/corporate-gifting", label: "Corporate Gifting" },
  { to: "/custom-gifting", label: "Custom Gifting" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count } = useStore();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-[background-color,border-color] duration-500 ${
        scrolled || open ? "bg-[#121212]/90 backdrop-blur-xl border-white/10" : "bg-[#121212]/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="px-4 sm:px-8 lg:px-16 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 group">
          <span className="w-2 h-2 bg-champagne inline-block group-hover:rotate-45 transition-transform duration-500" />
          <span className="font-serif text-lg sm:text-xl tracking-[0.18em] text-ivory">THE GOOD GRID</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] font-mono uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive ? "text-champagne" : "text-ivory/60 hover:text-ivory"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/gifting-list"
            data-testid="nav-gifting-list"
            className="relative hidden sm:flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-ivory/70 hover:text-ivory transition-colors px-3 py-2"
          >
            <ClipboardList size={15} strokeWidth={1.5} />
            <span className="hidden xl:inline">My Gifting List</span>
            {count > 0 && (
              <span data-testid="gifting-list-count" className="absolute -top-1 -right-1 bg-champagne text-charcoal text-[10px] font-semibold w-4 h-4 flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </Link>
          <Link
            to="/request-quote"
            data-testid="nav-request-quote"
            className="hidden sm:flex items-center gap-1.5 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 hover:bg-champagne-light transition-colors duration-300"
          >
            Request a Quote <ArrowUpRight size={13} strokeWidth={2} />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-ivory p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#121212] border-t border-white/10 px-6 py-8 flex flex-col gap-5">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-serif text-2xl ${isActive ? "text-champagne" : "text-ivory"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex gap-3 pt-4 border-t border-white/10">
            <Link to="/gifting-list" data-testid="mobile-gifting-list" className="flex-1 text-center border border-white/20 text-ivory text-[11px] font-mono uppercase tracking-[0.2em] px-4 py-3">
              Gifting List ({count})
            </Link>
            <Link to="/request-quote" data-testid="mobile-request-quote" className="flex-1 text-center bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.2em] px-4 py-3">
              Request a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
